import React from 'react';

interface JobData {
  job_reference: string;
  date_of_survey: string;
  well_name: string;
  rig_no: string;
  service: string;
}

interface LocationAndDepthDetails {
  latitude_1: number;
  latitude_2: number;
  latitude_3: number;
  latitude_unit: string;
  longitude_1: number;
  longitude_2: number;
  longitude_3: number;
  longitude_unit: string;
  northing: string;
  northing_unit: string;
  easting: string;
  easting_unit: string;
  geodetic_system: string;
  geodetic_datum: string;
  map_zone: string;
  north_reference: string;
  grid_correction: number;
  grid_correction_unit: string;
  central_meridian: number;
  central_meridian_unit: string;
  elevation_referred_to: string;
  ground_level: number;
  ground_level_unit: string;
  reference_datum: string;
  reference_height: number;
  reference_height_unit: string;
  reference_elevation: number;
  reference_elevation_unit: string;
  survey_calculation_method: string;
}

interface EquipmentDetails {
  survey_probe_type1: string;
  survey_probe_type2: string;
  probe_id1: string;
  probe_id2: string;
  error_model1: string;
  error_model2: string;
  displacement_referred_to: string;
  vertical_section_reference: string;
  vertical_section_azimuth: number;
  vertical_section_azimuth_unit: string;
}

interface SurveyDetails {
  well_type: string;
  hole_section: string;
  minimum_id: string;
  survey_tool_type: string;
  survey_run_in: string;
  survey_from: number;
  survey_from_unit: string;
  survey_to: number;
  survey_to_unit: string;
  distance_unit: string;
  survey_footage: number;
  reference_height: number;
  drill_floor_elevation: number;
}

interface TieOnDetails {
  measured_depth: number;
  measured_depth_unit: string;
  inclination: number;
  inclination_unit: string;
  azimuth: number;
  azimuth_unit: string;
  true_vertical_depth: number;
  true_vertical_depth_unit: string;
  latitude: number;
  latitude_unit: string;
  departure: number;
  depature_unit: string;
  data_obtained_by: string;
}

interface SurveyReportProps {
  jobdata: JobData;
  location_and_depth_details: LocationAndDepthDetails;
  equipment_details: EquipmentDetails;
  survey_details: SurveyDetails;
  tie_on_details: TieOnDetails;
}

const SurveyReport: React.FC<SurveyReportProps> = ({
  jobdata,
  location_and_depth_details,
  equipment_details,
  survey_details,
  tie_on_details,
}) => {
  // Helper function to convert DMS to decimal degrees
  const convertToDecimalDegrees = (degrees: number, minutes: number, seconds: number): number => {
    return degrees + minutes / 60 + seconds / 3600;
  };

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: '2-digit',
    });
  };

  // Format number with specified decimal places
  const formatNumber = (value: number, decimals: string): string => {
    const [min, max] = decimals.split('-').map(Number);
    return value.toFixed(max || min);
  };

  return (
    <div className="">
      <div className="!flex-grow">
        {/* JOB DETAILS STARTS */}
        <div className="flex justify-end mb-3 mt-5">
          <svg xmlns="http://www.w3.org/2000/svg" width="8cm" height="auto" viewBox="0 0 800 70">
            <defs>
              <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: 'rgb(173,173,173)', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: 'rgb(85,85,85)', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: 'rgb(212,97,97)', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <polygon points="25,0 800,0 800,70 0,70" fill="url(#gradient1)" />
            <text x="780" y="50" fontSize="40px" fill="white" textAnchor="end">
              Job Details
            </text>
          </svg>
        </div>
        
        <table className="table-fixed !w-full mt-2.5 border-collapse">
          <tbody>
            <tr className="h-[13.7pt]">
              <td className="w-[15.94%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Job # Reference</p>
              </td>
              <td colSpan={2} className="border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">
                  {jobdata.job_reference}
                </p>
              </td>
              <td className="w-[16.74%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Date of Survey</p>
              </td>
              <td className="w-[34.2%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">
                  {formatDate(jobdata.date_of_survey)}
                </p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[15.94%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Well Name&nbsp;</p>
              </td>
              <td colSpan={2} className="border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">
                  {jobdata.well_name}
                </p>
              </td>
              <td className="w-[16.74%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Rig #</p>
              </td>
              <td className="w-[34.2%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">
                  {jobdata.rig_no}
                </p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[15.94%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Survey Name</p>
              </td>
              <td colSpan={4} className="border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]"></p>
              </td>
            </tr>
            <tr className="h-[37.7pt]">
              <td colSpan={5} className="border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-white !mt-2">
                <div className="flex items-center justify-center whitespace-nowrap">
                  <p className="m-0 text-[12pt] !text-[#c00000]">
                    <strong>Type of Service Performed:&nbsp;&nbsp;</strong>
                  </p>
                  <p className="m-0 text-[18pt] !text-[#c00000]">
                    <strong>{jobdata.service}</strong>
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        {/* GEODETIC LOCATION AND DEPTH REFERENCE STARTS */}
        <div className="flex justify-end mb-3 !mt-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="8cm" height="auto" viewBox="0 0 800 70">
            <defs>
              <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: 'rgb(173,173,173)', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: 'rgb(85,85,85)', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: 'rgb(212,97,97)', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <polygon points="25,0 800,0 800,70 0,70" fill="url(#gradient2)" />
            <text x="780" y="50" fontSize="40px" fill="white" textAnchor="end">
              Geodetic Location & Depth Reference
            </text>
          </svg>
        </div>

        <table className="table-fixed !w-full mt-2.0 border-collapse">
          <tbody>
            <tr className="h-[13.7pt]">
              <td
                rowSpan={2}
                className="w-[11.1%] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]"
              >
                <p className="m-0 text-center text-[9pt] text-white whitespace-nowrap">
                  Well Centre <br /> Coordinates
                </p>
              </td>
              <td className="w-[9.52%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-center text-[9pt] text-white">Latitude</p>
              </td>
              <td className="w-[3.7%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">{location_and_depth_details.latitude_1}&nbsp;</p>
              </td>
              <td className="w-[2.56%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">°</p>
              </td>
              <td className="w-[3.7%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">{location_and_depth_details.latitude_2}&nbsp;</p>
              </td>
              <td className="w-[2.42%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">'</p>
              </td>
              <td className="w-[6.64%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">{formatNumber(location_and_depth_details.latitude_3, '1.3-3')}&nbsp;</p>
              </td>
              <td className="w-[2.7%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">"</p>
              </td>
              <td className="w-[3.08%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-[9pt]">{location_and_depth_details.latitude_unit}</p>
              </td>
              <td className="w-[2.84%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">=</p>
              </td>
              <td className="w-[11.88%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-right text-[9pt]">
                  <strong>
                    {formatNumber(
                      convertToDecimalDegrees(
                        location_and_depth_details.latitude_1,
                        location_and_depth_details.latitude_2,
                        location_and_depth_details.latitude_3
                      ),
                      '1.7-7'
                    )}
                  </strong>
                </p>
              </td>
              <td className="w-[3.68%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-[9pt]"><strong>°{location_and_depth_details.latitude_unit}</strong></p>
              </td>
              <td rowSpan={2} className="w-[11.1%] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-center text-[9pt] text-white">UTM Coordinates</p>
              </td>
              <td className="w-[8.68%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-center text-[9pt] text-white">Northing</p>
              </td>
              <td className="w-[11.88%] border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-right text-[9pt]"><strong>{location_and_depth_details.northing}</strong></p>
              </td>
              <td className="w-[4.0%] border-l-[0.75pt] border-b-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-[9pt]"><strong>{location_and_depth_details.northing_unit}</strong></p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[9.52%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-center text-[9pt] text-white">Longitude</p>
              </td>
              <td className="w-[3.7%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">{location_and_depth_details.longitude_1}</p>
              </td>
              <td className="w-[2.56%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">°</p>
              </td>
              <td className="w-[3.7%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">{location_and_depth_details.longitude_2}</p>
              </td>
              <td className="w-[2.42%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">'</p>
              </td>
              <td className="w-[6.64%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">{formatNumber(location_and_depth_details.longitude_3, '1.3-3')}</p>
              </td>
              <td className="w-[2.7%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">"</p>
              </td>
              <td className="w-[3.08%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-[9pt]">{location_and_depth_details.longitude_unit}</p>
              </td>
              <td className="w-[2.84%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-center text-[9pt]">=</p>
              </td>
              <td className="w-[11.88%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-right text-[9pt]">
                  <strong>
                    {formatNumber(
                      convertToDecimalDegrees(
                        location_and_depth_details.longitude_1,
                        location_and_depth_details.longitude_2,
                        location_and_depth_details.longitude_3
                      ),
                      '1.7-7'
                    )}
                  </strong>
                </p>
              </td>
              <td className="w-[3.68%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-[9pt]"><strong>°{location_and_depth_details.longitude_unit}</strong></p>
              </td>
              <td className="w-[8.68%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-center text-[9pt] text-white">Easting</p>
              </td>
              <td className="w-[11.88%] border-t-[0.75pt] border-r-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-right text-[9pt]"><strong>{location_and_depth_details.easting}</strong></p>
              </td>
              <td className="w-[4.0%] border-t-[0.75pt] border-l-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-gray-200">
                <p className="m-0 text-[9pt]"><strong>{location_and_depth_details.easting_unit}</strong></p>
              </td>
            </tr>
          </tbody>
        </table>

        <table className="table-fixed !w-full mt-5 border-collapse">
          <tbody>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Geodetic System&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                  <p className="m-0 text-[9pt] whitespace-nowrap">
                    {location_and_depth_details.geodetic_system}
                  </p>
                </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Elevation Referred</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.elevation_referred_to}</p>
              </td>
              
              
              
              
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Map Zone&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.map_zone}</p>
              </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Reference datum&nbsp;</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.reference_datum}&nbsp;</p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">North Reference&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.north_reference}</p>
              </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Reference Height&nbsp;</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{survey_details.reference_height}&nbsp;m</p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Grid Correction&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.grid_correction}</p>
              </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                  <p className="m-0 text-[9pt] text-white whitespace-nowrap">Reference Elevation&nbsp;</p>
                </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{survey_details.drill_floor_elevation}&nbsp; m</p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Central Meridian&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.central_meridian}</p>
              </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Survey Calculation&nbsp;</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.survey_calculation_method}&nbsp;</p>
              </td>
            </tr>
           
            
          </tbody>
        </table>

        {/* EQUIPMENT DETAILS STARTS */}
        <div className="flex mb-3 mt-5">
          <svg xmlns="http://www.w3.org/2000/svg" width="8cm" height="auto" viewBox="0 0 800 70">
            <defs>
              <linearGradient id="gradientinverse1" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: 'rgb(212,97,97)', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: 'rgb(85,85,85)', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: 'rgb(173,173,173)', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <polygon points="0,0 800,0 775,70 0,70" fill="url(#gradientinverse1)" />
            <text x="20" y="50" fontSize="40px" fill="white">
              Equipment Details
            </text>
          </svg>
          
        </div>
        <table className="table-fixed !w-full mt-5 border-collapse">
          <tbody>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">survey Probe type&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                  <p className="m-0 text-[9pt] whitespace-nowrap">
                    {equipment_details.survey_probe_type1}
                  </p>
                </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">displacement</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{equipment_details.displacement_referred_to}</p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Probe ID&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                  <p className="m-0 text-[9pt] whitespace-nowrap">
                    {equipment_details.probe_id1}
                  </p>
                </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Elevation Referred</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.elevation_referred_to}</p>
              </td>
            </tr>
            <tr className="h-[13.7pt]">
              <td className="w-[20.78%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Error Modal&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[125pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                  <p className="m-0 text-[9pt] whitespace-nowrap">
                    {equipment_details.error_model1}
                  </p>
                </td>
              <td className="w-[5.82%] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-top bg-white">
                <p className="m-0 text-[9pt]">&nbsp;</p>
              </td>
              <td colSpan={3} className="w-[85.6pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#808080]">
                <p className="m-0 text-[9pt] text-white">Elevation Referred</p>
              </td>
              <td colSpan={5} className="w-[147.05pt] border-[0.75pt] border-white pr-[5.03pt] pl-[5.03pt] align-middle bg-[#f2f2f2]">
                <p className="m-0 text-[9pt]">{location_and_depth_details.elevation_referred_to}</p>
              </td>
            </tr>
           
            
          </tbody>
        </table>

        {/* SURVEY DETAILS AND TIE ON DETAILS STARTS */}
        {/* SURVEY DETAILS STARTS */}
{/* SURVEY + TIE-ON PARALLEL */}
<div className="flex justify-between gap-5 mt-5">
  {/* Survey Details */}
  <div className="w-1/2">
    <div className="mb-3">
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="auto" viewBox="0 0 800 70">
        <defs>
          <linearGradient id="gradientinverse2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'rgb(212,97,97)', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: 'rgb(85,85,85)', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: 'rgb(173,173,173)', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <polygon points="0,0 800,0 775,70 0,70" fill="url(#gradientinverse2)" />
        <text x="20" y="50" fontSize="40px" fill="white">
          Survey Details
        </text>
      </svg>
    </div>

    <table className="table-fixed !w-full mt-2.5 border-collapse">
      <tbody>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Well Type</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.well_type}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Hole Section</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.hole_section}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Minimum ID</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.minimum_id}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Survey Tool Type</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.well_type}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Survey Performed In</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.survey_run_in}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Survey From</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.survey_from} {survey_details.survey_from_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Survey Footage</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.survey_to} {survey_details.survey_to_unit}
            </p>
          </td>
        </tr>
        
        {/* Add more survey rows */}
      </tbody>
    </table>
  </div>

  {/* Tie-On Details */}
  <div className="w-1/2 ml-5">
    <div className="mb-3">
      <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="auto" viewBox="0 0 800 70">
        <defs>
          <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: 'rgb(173,173,173)', stopOpacity: 1 }} />
            <stop offset="50%" style={{ stopColor: 'rgb(85,85,85)', stopOpacity: 1 }} />
            <stop offset="100%" style={{ stopColor: 'rgb(212,97,97)', stopOpacity: 1 }} />
          </linearGradient>
        </defs>
        <polygon points="25,0 800,0 800,70 0,70" fill="url(#gradient3)" />
        <text x="780" y="50" fontSize="40px" fill="white" textAnchor="end">
          Tie-On Details
        </text>
      </svg>
    </div>

    <table className="table-fixed !w-full mt-2.5 border-collapse">
      <tbody>
        <tr className="h-[13.7pt]">
          <td className="w-[40%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Measured Depth</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {tie_on_details.measured_depth} {tie_on_details.measured_depth_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Inclination</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {tie_on_details.inclination} °{tie_on_details.inclination_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Azimuth</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {tie_on_details.azimuth} °{tie_on_details.azimuth_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">True Vertical Depth</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {tie_on_details.true_vertical_depth} {tie_on_details.true_vertical_depth_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Latitude(+N/-S)</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {tie_on_details.latitude} {tie_on_details.latitude_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Departure(+E/-W)</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {tie_on_details.departure} {tie_on_details.depature_unit}
            </p>
          </td>
        </tr>
        <tr className="h-[13.7pt]">
          <td className="w-[43%] border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#808080]">
            <p className="m-0 text-[9pt] text-white whitespace-nowrap">Well Type</p>
          </td>
          <td className="border-[0.75pt] border-white px-[5.03pt] align-middle bg-[#f2f2f2]">
            <p className="m-0 text-[9pt] whitespace-nowrap">
              {survey_details.well_type}
            </p>
          </td>
        </tr>
        {/* Add more tie-on rows */}
      </tbody>
    </table>
  </div>
</div>



        {/* OVERALL SURVEY QC ANALYSIS */}
        <div className="flex justify-end mb-3 !pt-5" style={{ width: '100%' }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="8cm" height="auto" viewBox="0 0 800 70">
            <defs>
              <linearGradient id="gradient4" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" style={{ stopColor: 'rgb(173, 173, 173)', stopOpacity: 1 }} />
                <stop offset="50%" style={{ stopColor: 'rgb(85, 85, 85)', stopOpacity: 1 }} />
                <stop offset="100%" style={{ stopColor: 'rgb(212, 97, 97)', stopOpacity: 1 }} />
              </linearGradient>
            </defs>
            <polygon points="25,0 800,0 800,70 0,70" fill="url(#gradient4)" />
            <text x="780" y="50" fontSize="40px" fill="white" textAnchor="end">
              Overall Survey QC Analysis
            </text>
          </svg>
        </div>

        <div>
          <table className="table-fixed !text-center min-w-full !font-normal mt-2" cellSpacing="0" cellPadding="0">
            <thead>
              <tr className="table-primary border-b border-defaultborder !heigth-[20.25pt]">
                <th scope="col" className="!text-center border-r-[0.75pt] border-b-[0.75pt] border-[#ffffff] align-middle bg-white"></th>
                <th scope="col" className="!text-center border-r-[0.75pt] py-[1pt] border-b-[0.75pt] border-[#ffffff] align-middle bg-gray-200">
                  <p className="m-0 text-[8pt]">
                    <span className="!font-normal">Process Workflow</span>
                  </p>
                </th>
                <th scope="col" className="!text-center border-r-[0.75pt] py-[1pt] border-b-[0.75pt] border-[#ffffff] align-middle bg-gray-200">
                  <p className="m-0 text-[8pt]">
                    <span className="!font-normal">Sensor Calibration</span>
                  </p>
                </th>
                <th scope="col" className="!text-center border-r-[0.75pt] py-[1pt] border-b-[0.75pt] border-[#ffffff] align-middle bg-gray-200">
                  <p className="m-0 text-[8pt]">
                    <span className="!font-normal">Comparitive QC</span>
                  </p>
                </th>
                <th scope="col" className="!text-center border-r-[0.75pt] py-[1pt] border-b-[0.75pt] border-[#ffffff] align-middle bg-gray-200">
                  <p className="m-0 text-[8pt]">
                    <span className="!font-normal">Gyrocompass QC</span>
                  </p>
                </th>
                <th scope="col" className="!text-center border-r-[0.75pt] py-[1pt] border-b-[0.75pt] border-[#ffffff] align-middle bg-gray-200">
                  <p className="m-0 text-[8pt]">
                    <span className="!font-normal">Secondary QC</span>
                  </p>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-defaultborder text-[9pt] align-baseline" style={{ height: '14.25pt' }}>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#dc2626] text-[#ffffff]">
                  QC Confidence
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  100%
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  97.8%
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  100%
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  91.25%
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#a8a8a8]">
                  95%
                </td>
              </tr>
              <tr className="border-b border-defaultborder text-[9pt]" style={{ height: '14.25pt' }}>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#dc2626] text-[#ffffff]">
                  Weightage
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle !bg-[#c3c3c3]">
                  0.5
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  2
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  2
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#c3c3c3]">
                  5
                </td>
                <td className="!px-1 py-[1.2px] !text-center border border-white px-[5.03pt] text-center align-middle bg-[#a8a8a8]">
                  0.5
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="flex justify-between items-center mt-3 !w-[70%] gap-2">
          <div>
              <span className="text-[9pt] pl-[10px] whitespace-nowrap">
                    Overall Survey QC Confidence
             </span>

          </div>
          <div className="!w-[50%]">
            <table 
              className="table-fixed" 
              cellSpacing="0" 
              cellPadding="0" 
              style={{
                width: '100%',
                marginTop: '10px',
                border: '0.75pt solid #ffffff',
                borderCollapse: 'collapse',
                float: 'left',
              }}
            >
              <tbody>
                <tr style={{ height: '20.25pt' }}>
                  <td className="border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-[#ffffff] pr-[5.03pt] pl-[5.03pt] bg-lime-100 text-center">
                    <p className="m-0 text-[10pt]">
                      <span>97%</span>
                    </p>
                  </td>
                  <td
  colSpan={2}
  className="border-r-[0.75pt] border-l-[0.75pt] border-b-[0.75pt] border-[#ffffff] pr-[5.03pt] pl-[5.03pt] bg-lime-400"
>
  <p className="m-0 text-[9pt] whitespace-nowrap ">
    <span className="text-[#ffffff]">SURVEY QUALIFIED</span>
  </p>
</td>

                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveyReport;